/**
 * `nextStep` — the pure resolver behind the lead's "what do I do next?" coach
 * (issue #980).
 *
 * Problem (from live use): once a lead has created the first outcome and the
 * tree canvas appears, ALL guidance stops. The one-time empty-workspace
 * coach-mark (#932) teaches the four-step loop, but it is gone the moment a
 * root exists — exactly when decomposition (the hard, un-intuitive part)
 * begins. There is nothing telling the lead to break the outcome down, to
 * assign someone, or to move state as work happens.
 *
 * This module reads the LIVE tree and resolves the single most useful next
 * move, so the coach can advance as the user acts (named outcome → break it
 * down → assign → move state). It is deliberately pure + dependency-free: no
 * React, no store, no copy — just the structural decision. The copy lives in
 * the `<NextStepCoach/>` component; the brand-firewall sweep covers it there.
 *
 * The decision is intentionally about the WHOLE map's shape, not any one
 * person — it never names or ranks a member (§A9). It answers "the plan is
 * missing a stage of the loop" the same way the empty-workspace coach-mark
 * frames the loop.
 *
 * Clean-code note (founder standard, #725): the order of the checks IS the
 * onboarding loop, top to bottom. Each case returns once; the first unmet
 * stage wins. When every stage is met the map is "underway" → no coach.
 */

import type { TreeNode } from "../tree/buildTree";

/**
 * The stages of the authoring loop, in order. `underway` is the terminal
 * "nothing to nudge" state (the coach hides). The non-terminal stages each
 * map to one calm line of guidance in `<NextStepCoach/>`.
 */
export type NextStep =
  | "decompose" // a root exists but has no sub-tasks yet → break it down
  | "assign" // sub-tasks exist but no one is assigned anywhere → put people on them
  | "set-state" // people are assigned but everything is still "not started" → move state
  | "underway"; // the loop is rolling — nothing to nudge

/**
 * Walk every node once, collecting the three facts the loop turns on. One
 * pass keeps this O(n) and the logic obvious (no repeated tree walks).
 */
interface TreeFacts {
  /** Any non-root node exists (the outcome has been broken down at least once). */
  hasSubtasks: boolean;
  /** Any node carries at least one assignee. */
  hasAnyAssignment: boolean;
  /** Any node has moved off "Not started" (work has visibly begun). */
  hasAnyProgress: boolean;
}

function collectFacts(roots: readonly TreeNode[]): TreeFacts {
  const facts: TreeFacts = {
    hasSubtasks: false,
    hasAnyAssignment: false,
    hasAnyProgress: false,
  };

  const visit = (node: TreeNode, isRoot: boolean) => {
    if (!isRoot) facts.hasSubtasks = true;
    if (node.assigneeIds.length > 0) facts.hasAnyAssignment = true;
    if (node.state !== "planned") facts.hasAnyProgress = true;
    for (const child of node.children) visit(child, false);
  };

  for (const root of roots) visit(root, true);
  return facts;
}

/**
 * Resolve the lead's single next move from the live tree. Returns `null` when
 * the tree is empty (the empty workspace owns guidance there — the coach must
 * not double up). Otherwise returns the first unmet stage of the loop, or
 * `"underway"` once the loop is rolling.
 */
export function resolveNextStep(roots: readonly TreeNode[]): NextStep | null {
  if (roots.length === 0) return null;

  const { hasSubtasks, hasAnyAssignment, hasAnyProgress } = collectFacts(roots);

  // Stage order = the loop order. The first unmet stage is the next move.
  if (!hasSubtasks) return "decompose";
  if (!hasAnyAssignment) return "assign";
  if (!hasAnyProgress) return "set-state";
  return "underway";
}
